// 月別アーカイブ（docs/29）API クライアント

import type { MonthlyRecord, MonthIndexEntry, Statement } from "../types";
import { noteAuthRequired } from "./authSignal";

// 月インデックス取得（月セレクタ用）
export async function fetchMonthIndex(): Promise<{ ok: boolean; months: MonthIndexEntry[]; error?: string }> {
  try {
    const r = await fetch("/api/archive/list");
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, months: [], error: "API未提供（ローカル環境）" };
    const data = (await r.json()) as { ok: boolean; months?: MonthIndexEntry[]; error?: string };
    return { ok: data.ok, months: data.months || [], error: data.error };
  } catch (e) {
    return { ok: false, months: [], error: e instanceof Error ? e.message : String(e) };
  }
}

// 過去月のレコード取得
export async function fetchMonthRecord(month: string): Promise<{ ok: boolean; record: MonthlyRecord | null; error?: string }> {
  try {
    const r = await fetch(`/api/archive/get?month=${encodeURIComponent(month)}`);
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, record: null, error: "API未提供（ローカル環境）" };
    const data = (await r.json()) as { ok: boolean; record?: MonthlyRecord | null; error?: string };
    return { ok: data.ok, record: data.record ?? null, error: data.error };
  } catch (e) {
    return { ok: false, record: null, error: e instanceof Error ? e.message : String(e) };
  }
}

// 月を確定 → スナップショット保存
export async function saveMonthRecord(
  record: MonthlyRecord
): Promise<{ ok: boolean; entry?: MonthIndexEntry; error?: string }> {
  try {
    const r = await fetch("/api/archive/save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ month: record.month, record }),
    });
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, error: "API未提供（ローカル環境）。本番（Cloudflare）でのみ保存できます。" };
    const data = (await r.json()) as { ok: boolean; entry?: MonthIndexEntry; error?: string };
    return { ok: data.ok, entry: data.entry, error: data.error };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// 確定済みの過去月で1人分だけ訂正する（本城さん要望 2026-10-06）。
// expectedSavedAt は画面で開いたときのレコードの savedAt（開いた後に別の方が保存し直していたら 409）
export async function correctArchivedStatement(
  month: string,
  statement: Statement,
  expectedSavedAt: string,
  reason: string
): Promise<{ ok: boolean; entry?: MonthIndexEntry; record?: MonthlyRecord; error?: string; conflict?: boolean }> {
  try {
    const r = await fetch("/api/archive/correct", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ month, driverId: statement.driverId, expectedSavedAt, statement, reason }),
    });
    noteAuthRequired(r);
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, error: "API未提供（ローカル環境）。本番（Cloudflare）でのみ保存できます。" };
    const data = (await r.json()) as { ok: boolean; entry?: MonthIndexEntry; record?: MonthlyRecord; error?: string };
    return { ok: data.ok, entry: data.entry, record: data.record, error: data.error, conflict: r.status === 409 };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
